import { useEffect, useRef, useState } from "react"
import { useInView } from "@/shared/hooks/useInView"
import { Button } from "@/shared/ui/Button"
import { Reveal } from "@/shared/ui/Reveal"
import { Section } from "@/shared/ui/Section"
import { SectionHeading } from "@/shared/ui/SectionHeading"
import { ArrowRight, CheckCircle } from "@/shared/ui/icons"
import { AccreditationForm } from "@/features/accreditation/components/AccreditationForm"
import { AccreditationSuccess } from "@/features/accreditation/components/AccreditationFeedback"
import { useAccreditationForm } from "@/features/accreditation/useAccreditationForm"

export const ACCREDITATION_ANCHOR = "credenciamento"

/** O que a clinica ganha ao entrar na rede — o convite antes da ficha. */
const INVITATION_POINTS = [
  "Fluxo constante de tutores com plano ativo, sem custo de captação.",
  "Sua unidade listada na busca de rede credenciada do site.",
  "Condições e repasses acordados antes de qualquer atendimento.",
]

/**
 * Credenciamento de rede.
 *
 * A ficha comeca fechada, e isto e uma decisao de pagina, nao de estilo: esta e
 * uma landing page para tutores, e quarenta campos de um formulario B2B no meio
 * dela empurram planos e FAQ para muito longe de quem veio contratar. Fechada,
 * a secao ocupa uma faixa; aberta, entrega a ficha inteira.
 *
 * Quem chega por `dimpetrn.com.br/#credenciamento` — o link do rodape, ou um
 * endereco passado por mensagem a uma clinica — encontra a ficha ja aberta.
 */
export function AccreditationSection() {
  const { ref, inView } = useInView()
  const form = useAccreditationForm()
  const [open, setOpen] = useState(false)
  const formRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (window.location.hash === `#${ACCREDITATION_ANCHOR}`) setOpen(true)
  }, [])

  const openForm = () => {
    setOpen(true)

    // O timeout espera o React pintar a ficha: sem ele o scroll acontece contra
    // o layout antigo, em que a ficha ainda nao ocupava altura nenhuma.
    window.setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100)
  }

  return (
    <Section ref={ref} id={ACCREDITATION_ANCHOR} tone="off-white">
      <Reveal inView={inView} className="mb-10 max-w-2xl">
        <SectionHeading
          eyebrow="Seja um credenciado"
          title="Faça parte da rede DIMPET"
          description="Clínicas, consultórios, hospitais e médicos-veterinários autônomos podem se credenciar à rede prestadora. Preencha a ficha e nossa equipe retorna com os próximos passos."
        />
      </Reveal>

      <div ref={formRef}>
        {form.status === "success" && form.receipt ? (
          <AccreditationSuccess receipt={form.receipt} onReset={form.reset} />
        ) : open ? (
          <Reveal inView className="rounded-2xl bg-white border border-verde-profundo/8 p-6 sm:p-8 lg:p-10">
            <AccreditationForm form={form} />
          </Reveal>
        ) : (
          <Reveal inView={inView} delayMs={80}>
            <div className="rounded-2xl bg-white border border-verde-profundo/8 p-6 sm:p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">
              <ul className="flex flex-col gap-3 flex-1">
                {INVITATION_POINTS.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <span className="mt-0.5 shrink-0 text-dourado">
                      <CheckCircle />
                    </span>
                    <span className="text-sm text-verde-profundo/70 leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>

              <div className="shrink-0 flex flex-col gap-2 items-start">
                <Button variant="primary" size="lg" onClick={openForm} icon={<ArrowRight />}>
                  Preencher ficha de credenciamento
                </Button>
                <p className="text-xs text-verde-profundo/40">Leva cerca de 5 minutos.</p>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </Section>
  )
}
